/* eslint-disable no-param-reassign */
import { type EChartsExtensionInstallRegisters } from 'echarts/types/src/extension';
import type { Payload } from 'echarts/types/src/util/types';
import type { ElementEvent } from 'zrender/lib/Element.js';
import type GlobalModel from 'echarts/types/src/model/Global';

import { Line } from 'echarts/lib/util/graphic';
import { ViewRootGroup } from 'echarts/lib/util/types';
import * as graphic from 'echarts/lib/util/graphic';
import * as zrUtil from 'zrender/lib/core/util.js';
import { type DvRadarSymbol } from './DvRadarView';
import { type DvRadarSeriesModelClazz as DvRadarSeriesModel } from './radarSeriesModel';

// 常量定义
const TRANSPARENT_COLOR = 'rgba(0,0,0,0)';
const FULL_CIRCLE = 2;
const HALF_CIRCLE = 2;

const CHILD_INDICES = {
  POLYLINE: 0,
  HIGHLIGHT_POLYGON: 3,
  BACKGROUND_POLYGON: 4,
  BACKGROUND_LINE: 5
} as const;

const DEFAULT_VALUES = {
  RADIUS: 100,
  OPACITY: 0.4,
  MAX_POLYGON_INDEX: 2
} as const;

type ActionPayload = Payload & {
  seriesIndex?: number | number[];
  seriesId?: string | string[];
  seriesName?: string | string[];
};

type MouseOverSectorPayload = ActionPayload & {
  event: ElementEvent;
  line: Line;
  group: ViewRootGroup;
};

// 辅助函数：设置符号样式
function setSymbolStyle(
  symbolPath: DvRadarSymbol,
  colorList: string[],
  groupIndex: number,
  payload: MouseOverSectorPayload,
  sectorSelector: any
): void {
  symbolPath.eachChild((dot: DvRadarSymbol, index: number) => {
    const hoverId = (payload.event.target as any).hover_id;
    if (index === hoverId) {
      if (sectorSelector.symbolStyle) {
        dot.attr({ style: sectorSelector.symbolStyle });
      } else {
        dot.attr({ style: { fill: 'white', stroke: colorList[groupIndex] } });
      }
    }
  });
}

// 辅助函数：清除符号样式
function clearSymbolStyle(
  symbolGroup: graphic.Group,
  colorList: string[],
  groupIndex: number
): void {
  symbolGroup.eachChild((symbolPath: DvRadarSymbol) => {
    if (symbolPath.type === 'group') {
      symbolPath.eachChild((dot: DvRadarSymbol) => {
        dot.attr({ style: { fill: colorList[groupIndex], stroke: null } });
      });
    }
  });
}

// 辅助函数：创建扇形裁剪路径
function createSectorClip(
  x: number,
  y: number,
  r: number,
  sectorStartAngle: number,
  angleRange: number
): graphic.Sector {
  return new graphic.Sector({
    shape: {
      cx: x,
      cy: y,
      r,
      // 内半径，要绘制扇形而非圆环，设置为0
      r0: 0,
      startAngle: sectorStartAngle,
      endAngle: sectorStartAngle + angleRange
    }
  });
}

// 辅助函数：创建自定义裁剪路径
function createCustomClipPath(
  x: number,
  y: number,
  r: number,
  sectorStartAngle: number,
  angleRange: number
): graphic.Path {
  return new graphic.Path({
    buildPath(ctx, shape) {
      const {
        cx = 0,
        cy = 0,
        r: radius = DEFAULT_VALUES.RADIUS,
        sectorStart = 0,
        sectorEnd = Math.PI / HALF_CIRCLE
      } = shape || {};

      ctx.moveTo(cx, cy);
      // 连到扇形起始点
      ctx.lineTo(
        (cx as number) + (radius as number) * Math.cos(sectorStart as number),
        (cy as number) + (radius as number) * Math.sin(sectorStart as number)
      );
      // 沿着圆弧到扇形结束点（走大弧）
      ctx.arc(
        cx as number,
        cy as number,
        radius as number,
        sectorStart as number,
        (sectorEnd as number) - Math.PI * FULL_CIRCLE,
        true
      );
      // 回到圆心，自动闭合
      ctx.closePath();
    },
    shape: {
      cx: x,
      cy: y,
      r,
      sectorStart: sectorStartAngle,
      sectorEnd: sectorStartAngle + angleRange
    }
  });
}

// 辅助函数：创建圆形裁剪路径
function createCircleClip(x: number, y: number, r: number): graphic.Circle {
  return new graphic.Circle({
    shape: {
      cx: x,
      cy: y,
      r
    }
  });
}

// 辅助函数：应用高亮样式
function applyHighlightStyles(
  itemGroup: graphic.Group,
  idx: number,
  data: any,
  seriesModel: DvRadarSeriesModel,
  clipPath: graphic.Path,
  clip: graphic.Sector
): void {
  const polyline = itemGroup?.childAt?.(CHILD_INDICES.POLYLINE) as graphic.Polygon;
  const highlightPolygon = itemGroup?.childAt?.(CHILD_INDICES.HIGHLIGHT_POLYGON) as graphic.Polygon;
  const backgroundPolygon = itemGroup?.childAt?.(
    CHILD_INDICES.BACKGROUND_POLYGON
  ) as graphic.Polygon;
  const backgroundLine = itemGroup?.childAt?.(CHILD_INDICES.BACKGROUND_LINE) as graphic.Polygon;

  const itemStyle = data.getItemVisual(idx, 'style');
  const color = itemStyle.fill;
  const { highlight, lineStyle } = seriesModel.option;

  if (!highlight || !highlight.show) {
    return;
  }

  backgroundPolygon?.useStyle?.(
    zrUtil.defaults(
      { ...highlight?.backgroundStyle },
      {
        fill: color,
        opacity: DEFAULT_VALUES.OPACITY
      }
    )
  );

  backgroundLine?.useStyle?.(
    zrUtil.defaults(
      {
        ...(highlight?.backgroundStyle || {}),
        fill: 'none',
        stroke: highlight?.backgroundStyle?.fill,
        lineWidth: highlight?.lineStyle?.width ?? lineStyle?.width
      },
      {
        stroke: color,
        lineWidth: highlight?.lineStyle?.width ?? lineStyle?.width
      }
    )
  );

  backgroundPolygon?.setClipPath?.(clipPath);
  highlightPolygon?.setClipPath?.(clip);
  polyline?.setClipPath?.(clip);
  backgroundLine?.setClipPath?.(clipPath);

  if (itemGroup.type === 'circle') {
    itemGroup?.useStyle?.(
      zrUtil.defaults({ ...highlight?.maskStyle }, { fill: '#000', opacity: 0.2 })
    );
    itemGroup?.setClipPath?.(clipPath);
  }
}

// 辅助函数：移除高亮样式
function removeHighlightStyles(itemGroup: graphic.Group, circle: graphic.Circle): void {
  const backgroundPolygon = itemGroup?.childAt?.(
    CHILD_INDICES.BACKGROUND_POLYGON
  ) as graphic.Polygon;
  const backgroundLine = itemGroup?.childAt?.(CHILD_INDICES.BACKGROUND_LINE) as graphic.Polygon;
  const highlightPolygon = itemGroup?.childAt?.(CHILD_INDICES.HIGHLIGHT_POLYGON) as graphic.Polygon;
  const polyline = itemGroup?.childAt?.(CHILD_INDICES.POLYLINE) as graphic.Polygon;

  backgroundPolygon?.useStyle?.(zrUtil.defaults({ fill: 'none' }, {}));
  backgroundLine?.useStyle?.(zrUtil.defaults({ fill: 'none', stroke: 'transparent' }, {}));
  backgroundPolygon?.setClipPath?.(circle);
  highlightPolygon?.setClipPath?.(circle);
  polyline?.setClipPath?.(circle);

  if (itemGroup.type === 'circle') {
    itemGroup.useStyle?.(zrUtil.defaults({ fill: 'none' }, {}));
  }
}

// 主要函数：处理高亮
function handleHighlight(
  seriesModel: DvRadarSeriesModel,
  payload: MouseOverSectorPayload,
  inside: boolean
): void {
  const { highlight } = seriesModel.option;
  const hoverId = (payload.event.target as any).hover_id;
  const data = seriesModel.getData();

  if (!highlight || highlight.show === false) {
    return;
  }

  if (highlight.show === undefined) {
    highlight.show = false;
  }

  if (!highlight.show) {
    return;
  }

  const x = seriesModel.coordinateSystem.cx;
  const y = seriesModel.coordinateSystem.cy;
  const { r } = seriesModel.coordinateSystem;
  const dimensionNumber = seriesModel.coordinateSystem.dimensions.length;
  const angleRange = (Math.PI * FULL_CIRCLE) / dimensionNumber;
  const sectorStartAngle =
    (-1 / HALF_CIRCLE) * Math.PI - angleRange / HALF_CIRCLE + angleRange * hoverId;
  const clipOffset = highlight?.clipOffset ?? 0;

  const clip = createSectorClip(x, y, r + clipOffset, sectorStartAngle, angleRange);
  const clipPath = createCustomClipPath(x, y, r + clipOffset, sectorStartAngle, angleRange);
  const circle = createCircleClip(x, y, r + clipOffset);

  data.eachItemGraphicEl((itemGroup: graphic.Group, idx: number) => {
    if (inside) {
      applyHighlightStyles(itemGroup, idx, data, seriesModel, clipPath, clip);
    } else {
      removeHighlightStyles(itemGroup, circle);
    }
  });
}

// 辅助函数：处理扇形选择器
function handleSectorSelector(
  seriesModel: DvRadarSeriesModel,
  payload: MouseOverSectorPayload,
  colorList: string[]
): void {
  const { sectorSelector } = seriesModel.option;

  if (!sectorSelector) {
    return;
  }

  if (sectorSelector.show === undefined) {
    sectorSelector.show = true;
  }

  if (sectorSelector.show !== true) {
    return;
  }

  if (sectorSelector.sectorStyle) {
    // 将扇形的填充颜色修改为透明灰色
    (payload.event.target as any).setStyle(sectorSelector.sectorStyle);
  }

  const x = seriesModel.coordinateSystem.cx;
  const y = seriesModel.coordinateSystem.cy;
  const { r } = seriesModel.coordinateSystem;
  const dimensionNumber = seriesModel.coordinateSystem.dimensions.length;
  const angleRange = (Math.PI * FULL_CIRCLE) / dimensionNumber;

  if (sectorSelector.lineStyle) {
    const hoverId = (payload.event.target as any).hover_id;
    // 计算外侧弧线中点的坐标
    const midAngle = hoverId * angleRange - Math.PI / HALF_CIRCLE;
    const midX =
      (x as number) + ((r as number) + (sectorSelector.pointerOffset || 0)) * Math.cos(midAngle);
    const midY =
      (y as number) + ((r as number) + (sectorSelector.pointerOffset || 0)) * Math.sin(midAngle);
    payload.line.attr({ shape: { x1: x as number, y1: y as number, x2: midX, y2: midY } });
  }

  payload.group.eachChild((symbolGroup: graphic.Group, groupIndex: number) => {
    symbolGroup.eachChild((symbolPath: DvRadarSymbol, index: number) => {
      if (symbolPath.type === 'polygon' && index <= DEFAULT_VALUES.MAX_POLYGON_INDEX) {
        colorList[groupIndex] = symbolPath.style.fill;
      }
      if (symbolPath.type === 'group') {
        setSymbolStyle(symbolPath, colorList, groupIndex, payload, sectorSelector);
      }
    });
  });
}

// 主要函数：注册雷达图动作
export function installRadarAction(registers: EChartsExtensionInstallRegisters): void {
  // 记录不同series的颜色
  const colorList: string[] = [];

  // 注册鼠标悬停动作
  registers.registerAction(
    { type: 'radarMouseOverSector', update: 'none' },
    (payload: MouseOverSectorPayload, ecModel: GlobalModel) => {
      const result = ecModel.findComponents({
        mainType: 'series',
        subType: 'dvRadar',
        query: {
          seriesIndex: payload.seriesIndex,
          seriesId: payload.seriesId,
          seriesName: payload.seriesName
        }
      }) as DvRadarSeriesModel[];

      result.forEach(seriesModel => {
        handleHighlight(seriesModel, payload, true);
        handleSectorSelector(seriesModel, payload, colorList);
      });
    }
  );

  // 注册鼠标移出动作
  registers.registerAction(
    { type: 'radarMouseOutSector', update: 'none' },
    (payload: MouseOverSectorPayload, ecModel: GlobalModel) => {
      const result = ecModel.findComponents({
        mainType: 'series',
        subType: 'dvRadar',
        query: {
          seriesIndex: payload.seriesIndex,
          seriesId: payload.seriesId,
          seriesName: payload.seriesName
        }
      }) as DvRadarSeriesModel[];

      result.forEach(seriesModel => {
        handleHighlight(seriesModel, payload, false);

        // 获取被移出的扇形对象，恢复透明色
        const { target } = payload.event;
        if (payload.event.topTarget.type !== 'line') {
          (target as any).setStyle('fill', TRANSPARENT_COLOR);
          payload.line.attr({
            shape: {
              x1: seriesModel.coordinateSystem.cx,
              y1: seriesModel.coordinateSystem.cy,
              x2: seriesModel.coordinateSystem.cx,
              y2: seriesModel.coordinateSystem.cy
            }
          });
          payload.group.eachChild((symbolGroup: graphic.Group, groupIndex: number) => {
            clearSymbolStyle(symbolGroup, colorList, groupIndex);
          });
        }
      });
    }
  );
}
